import React, {useEffect, useState, useCallback} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Box, Button, Icon, Flex, Alert} from '@spothero/ui';
import IconArrowLeft from '@spothero/icons/arrow-left';
import IconTimes from '@spothero/icons/x';
import {
    PROPTYPE_EVENT_PACKAGE,
    PROPTYPE_EVENT_PACKAGE_VENUE,
} from '@/proptypes/index';
import SpotDetailsPlaceholder from 'common/spot-details/placeholder';
import SpotImages from 'common/spot-details/spot-images';
import FacilitySummary from 'common/facility-summary/v2';
import SpotDetailsReservationSummary from 'common/spot-details/reservation-summary';
import EventPrices from '../event-prices';
import PaymentMethodsIcons from 'common/payment-methods-icons';
import ThingsYouShouldKnow from 'common/spot-details/things-you-should-know';
import AmenitiesList from 'common/spot-details/amenities-list';
import AccessHours from 'common/spot-details/access-hours';
import GettingThere from 'common/spot-details/getting-there';
import HowToRedeem from 'common/spot-details/redemption-instructions';
import Reviews from 'common/spot-details/reviews';
import SpotNotes from 'common/spot-details/operator-and-admin-notes';
import FAQPanels from 'common/spot-details/faq';
import TrustSealValueProps from 'common/spot-details/trust-seal-and-value-props';
import useStickyScroll from 'hooks/use-sticky-scroll';
import {trackEventPackageSpotDetails} from 'segment/pages/spot-details';
import {getEventPackageFacilityDetailV2} from 'api/search-v2/event-packages/get-facility-detail';
import {CarouselVariant} from 'common/image-carousel';
import {FACILITY_SUMMARY_VIEW_TYPES} from 'common/facility-summary/utils';
import {usePrice} from 'hooks/spot/price/use-price';
import {useLabel} from 'hooks/spot/price/use-label';
import {useShowTotal} from 'store/selectors/hooks/useShowTotal';
import SpotDetailPriceBreakdownModal from 'common/spot-details/price-breakdown-modal/SpotDetailPriceBreakdownModal';

export const EventPackageSpotDetails = ({
    spotId,
    spotDetailReferrer,
    eventPackage,
    eventPackageVenue,
    onCloseClick,
    isAdmin,
}) => {
    // Spot Data
    const [isLoading, setIsLoading] = useState(true);
    const [hasLoadError, setHasLoadError] = useState(false);
    const [showPriceBreakdownModal, setShowPriceBreakdownModal] = useState(
        false
    );
    const [spot, setSpot] = useState(null);
    const priceFromHook = usePrice(spot);
    const showTotal = useShowTotal();
    const price = {
        ...priceFromHook,
        value: !showTotal
            ? priceFromHook.value * spot?.eventPackage?.events?.length
            : priceFromHook.value,
    };
    const label = useLabel({});
    // Below selector and if statement enable us to display the "total" of all the advertised prices for event-packages
    const trackSpotDetails = useCallback(
        spotData => {
            try {
                // Do not invoke tracking when Spot Details is closed (spotDetailReferrer is null)
                if (spotDetailReferrer) {
                    trackEventPackageSpotDetails({
                        eventPackage,
                        destination: eventPackageVenue,
                        spot: spotData,
                        spotDetailReferrer,
                    });
                }
            } catch (error) {
                console.log('Error capturing event', error); // eslint-disable-line no-console
            }
        },
        [eventPackage, eventPackageVenue, spotDetailReferrer]
    );

    const loadSpotInfo = useCallback(() => {
        getEventPackageFacilityDetailV2({
            eventPackageId: eventPackage.id,
            facilityId: spotId,
        })
            .then(spotData => {
                setSpot(spotData);
                setIsLoading(false);
                setHasLoadError(false);
                trackSpotDetails(spotData);
            })
            .catch(() => {
                setIsLoading(false);
                setHasLoadError(true);
            });
    }, [eventPackage.id, spotId, trackSpotDetails]);

    useEffect(() => {
        if (spotId) {
            loadSpotInfo();
        }
    }, [spotId, loadSpotInfo]);

    const onBookSpotClick = evt => {
        evt.preventDefault();

        const {
            selectedRate: {expressCheckoutUrl},
        } = spot;

        window.location.href = expressCheckoutUrl;
    };

    // logic for displaying different reservation summary view on scroll
    const [showStickySpotDetailsReservationSummary, onScroll] = useStickyScroll(
        '.SpotDetailsReservationSummary'
    );

    const spotUnavailable = !spot?.availability?.available;
    // Below this are helper functions for rendering all the sub-components
    const hasFacilityData = Boolean(spot);
    const hasFacilityAndRateData =
        Boolean(spot?.selectedRate) && !spotUnavailable;

    const renderSpotImages = spotData => {
        const {title, images} = spotData;

        return (
            <SpotImages
                key={spotId}
                images={images}
                alt={title}
                urlProperty="urlTemplate"
                carouselVariant={CarouselVariant.SINGLE}
            />
        );
    };

    const renderFacilitySummary = () => {
        return (
            <Box borderBottomWidth="1px" borderBottomColor="gray.medium">
                <FacilitySummary
                    isAdmin={isAdmin}
                    spot={spot}
                    showImages={false}
                    showPrice={false}
                    showFullTitle
                    showAdminLink
                    view={FACILITY_SUMMARY_VIEW_TYPES.SPOT_DETAIL}
                />
            </Box>
        );
    };

    const renderThingsYouShouldKnow = spotData => {
        const {facilityRestrictions, selectedRate} = spotData;
        const {amenities} = selectedRate;
        const hasInOutAmenity = Boolean(
            amenities?.find(amenity => amenity?.type === 'in-out')
        );

        return (
            <ThingsYouShouldKnow
                hasInOutAmenity={hasInOutAmenity}
                restrictions={facilityRestrictions}
            />
        );
    };

    const renderAmenities = spotData => {
        const {selectedRate} = spotData;
        const {amenities} = selectedRate;

        const rate = {
            amenities: amenities?.map(amenity => {
                return {
                    slug: amenity.type,
                    name: amenity.displayName,
                };
            }),
        };

        return <AmenitiesList rate={rate} isMonthly={false} />;
    };

    const renderAccessHours = spotData => {
        const {accessHoursFormattedV1} = spotData;

        const facility = {
            // eslint-disable-next-line camelcase
            access_hours_formatted: accessHoursFormattedV1,
        };

        return (
            <AccessHours
                rate={null}
                facility={facility}
                sectionTitle="Access Hours"
            />
        );
    };

    const renderRedemptionInstructions = spotData => {
        const {redemptionInstructions} = spotData;

        // need to un-camelcase some keys to fit existing component
        const formattedRedemptionInstructions = redemptionInstructions?.map(
            item => ({
                ...item,
                // eslint-disable-next-line camelcase
                illustration_id: item.illustrationId || item?.illustration?.id,
            })
        );

        return (
            Boolean(formattedRedemptionInstructions) && (
                <HowToRedeem
                    sectionTitle="How to Redeem"
                    instructions={formattedRedemptionInstructions}
                />
            )
        );
    };

    const renderGettingThere = spotData => {
        const {navigationTip} = spotData;

        return navigationTip && <GettingThere instructions={navigationTip} />;
    };

    const renderReviews = spotData => {
        const {
            rating: {average, count},
        } = spotData;

        return (
            <Reviews
                ratingInfo={{
                    /* eslint-disable camelcase */
                    number_of_ratings: count,
                    star_rating: average,
                    /* eslint-enable camelcase */
                }}
            />
        );
    };

    const renderSpotNotes = spotData => {
        const {operatorNotes, adminNotes} = spotData;

        return (
            <>
                <div
                    className="SpotDetails-section"
                    data-testid="EventPackageSpotDetails-SpotNotes"
                >
                    <h4 className="subtitle">Spot Notes (Admin)</h4>
                    <SpotNotes type="spot" notes={adminNotes} />
                </div>
                <div
                    className="SpotDetails-section"
                    data-testid="EventPackageSpotDetails-OperatorNotes"
                >
                    <h4 className="subtitle">Operator Notes (Admin)</h4>
                    <SpotNotes type="operator" notes={operatorNotes} />
                </div>
            </>
        );
    };

    const renderFooter = () => {
        return (
            <div className="SpotDetails-footer">
                <FAQPanels cancellationAllowed="no" />
                <TrustSealValueProps />
            </div>
        );
    };
    // End Spot Details sub-components
    const togglePriceBreakdownModal = () => {
        setShowPriceBreakdownModal(!showPriceBreakdownModal);
    };

    const reservationSummaryProps = {
        isLoadingRates: isLoading,
        displayPrice: price?.value,
        currencyType: price?.currencyCode,
        reservationLabel: 'Event Package',
        reservationDateTimeFormatted: spot?.eventPackage?.title,
        isReservationExtended: false,
        showSpotUnavailable: hasLoadError || spotUnavailable,
        onBookSpotClick,
        priceLabel: showTotal ? label : undefined,
        onShowRateDetailsClick: togglePriceBreakdownModal,
    };

    return (
        <>
            <Box
                className="EventPackageSpotDetails-map-overlay"
                onClick={onCloseClick}
                display={{base: 'none', tablet: 'block'}}
                position="absolute"
                top="0"
                left="0"
                bottom="0"
                right="0"
                backgroundColor="secondary.default"
                opacity=".5"
            />
            <Box
                position="absolute"
                top="0"
                left="0"
                bottom="0"
                width={{base: '100%', tablet: '512px'}}
                height="100%"
                overflowY="scroll"
                backgroundColor="white"
                onScroll={onScroll}
            >
                <Button
                    _hover={{
                        borderColor: 'transparent',
                    }}
                    onClick={onCloseClick}
                    variant="secondary"
                    aria-label="Close Spot Details"
                    position="absolute"
                    top={{base: '2', tablet: '4'}}
                    left={{base: '2', tablet: 'auto'}}
                    right={{base: 'auto', tablet: '4'}}
                    padding="0"
                    height="2.75rem"
                    width="2.75rem"
                    zIndex="layer2"
                >
                    <Box display={{base: 'block', tablet: 'none'}}>
                        <Icon as={IconArrowLeft} color="primary.default" />
                    </Box>
                    <Box display={{base: 'none', tablet: 'block'}}>
                        <Icon as={IconTimes} color="black" />
                    </Box>
                </Button>
                {hasFacilityData && renderSpotImages(spot)}
                {hasFacilityData && renderFacilitySummary(spot)}
                {hasFacilityData && (
                    <SpotDetailsReservationSummary
                        {...reservationSummaryProps}
                        isSticky={false}
                        isShowing={false}
                    />
                )}
                {hasFacilityData && (
                    <Flex
                        marginX={4}
                        marginBottom={4}
                        width={{base: 'auto', tablet: 64}}
                        justifyContent="center"
                    >
                        <PaymentMethodsIcons
                            showAlternatePaymentOptions
                            alignment="center"
                        />
                    </Flex>
                )}
                {isLoading && <SpotDetailsPlaceholder />}
                {hasLoadError && (
                    <Alert
                        status="error"
                        description="There was an error retrieving the facility
                                                                       information."
                        showIcon
                        buttonText="Retry"
                        marginX={4}
                        buttonProps={{
                            onClick: loadSpotInfo,
                        }}
                        width="auto"
                        borderRadius="md"
                    />
                )}
                {hasFacilityAndRateData && (
                    <EventPrices
                        events={spot.eventPackage.events}
                        timezone={eventPackageVenue.timezone}
                    />
                )}
                {hasFacilityAndRateData && renderThingsYouShouldKnow(spot)}
                {hasFacilityAndRateData && renderAmenities(spot)}
                {hasFacilityAndRateData && renderAccessHours(spot)}
                {hasFacilityData && renderRedemptionInstructions(spot)}
                {hasFacilityData && renderGettingThere(spot)}
                {hasFacilityData && renderReviews(spot)}
                {isAdmin && hasFacilityData && renderSpotNotes(spot)}
                {hasFacilityData && renderFooter()}
                {hasFacilityData && (
                    <SpotDetailsReservationSummary
                        {...reservationSummaryProps}
                        isSticky
                        isShowing={showStickySpotDetailsReservationSummary}
                    />
                )}
                {showPriceBreakdownModal && (
                    <SpotDetailPriceBreakdownModal
                        spot={spot}
                        onHidden={togglePriceBreakdownModal}
                    />
                )}
            </Box>
        </>
    );
};

EventPackageSpotDetails.propTypes = {
    spotId: PropTypes.string.isRequired,
    eventPackage: PROPTYPE_EVENT_PACKAGE.isRequired,
    eventPackageVenue: PROPTYPE_EVENT_PACKAGE_VENUE.isRequired,
    isAdmin: PropTypes.bool,
    onCloseClick: PropTypes.func.isRequired,
    spotDetailReferrer: PropTypes.string,
};

const mapStateToProps = state => {
    const {
        user: {data: user},
        spot: {spotDetailReferrer},
    } = state;

    return {
        isAdmin: user.isAdmin,
        spotDetailReferrer,
    };
};

export default connect(mapStateToProps)(EventPackageSpotDetails);
